import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { EMPTY, lastValueFrom, Observable, of, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { ApiBaseResponse } from "../api/api.types";
import {
  AuthApiLoginResponse,
  AuthApiPermissionCodeListResponse,
  AuthApiSiteListResponse,
} from "./auth-api.types";
import { AppState } from "../../app.reducer";
import { Store } from "@ngrx/store";
import { ApiActions } from "../api/api.actions";
import { AuthActions } from "./auth.actions";
import { Router } from "@angular/router";
import { isPlatformBrowser, isPlatformServer } from "@angular/common";
import { environment } from "../../../environments/environment";
import { ApiFilterTerm } from "../api/api.list";
import { SiteStorageService } from "./services/site-storage.service";
import { AuthSite } from "./models/auth-logspace-locator.model";

export interface AuthToken {
  accessToken: string;
  tokenExp: number;
  refreshToken: string;
}

const fHttpOptions = {
  headers: new HttpHeaders({
    "Content-Type": "application/json",
  }),
  observe: "response" as const,
  withCredentials: true,
};

@Injectable({
  providedIn: "root",
})
export class AuthService {
  baseUrl: string = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private siteStorageService: SiteStorageService,
  ) {}
  // private isAuthenticated: boolean = false;

  public login(
    identifier: string,
    password: string,
  ): Observable<AuthApiLoginResponse> {
    return this.http
      .post<AuthApiLoginResponse>(
        `${this.baseUrl}/auth/login/`,
        { identifier: identifier, password: password },
        fHttpOptions,
      )
      .pipe(
        // Note: post-login navigation is intentionally handled by
        // `navigateAfterAuthSuccess$` in auth.effects.ts (it reads
        // `returnUrl` from the URL and dispatches AFTER `loginSuccess` has
        // populated the store). Navigating here used to fire BEFORE the
        // store was updated, causing AuthGuard to bounce the user back to
        // /login — a race that produced flicker and intermittent
        // "logged-in but stuck on login" reports.
        map((resp) => {
          return resp.body as AuthApiLoginResponse;
        }),
      );
  }

  // login2(username: string, password: string): boolean {
  // 	// Check if the username and password are valid
  // 	if (username === 'admin' && password === 'password') {
  // 		this.isAuthenticated = true;
  // 		return true;
  // 	} else {
  // 		return false;
  // 	}
  // }

  public logout(): Observable<ApiBaseResponse> {
    // The API logout endpoint clears the HttpOnly BDPSSNID cookie via Set-Cookie header.
    // withCredentials: true ensures the cookie deletion is applied.
    return this.http
      .post<ApiBaseResponse>(`${this.baseUrl}/auth/logout/`, null, fHttpOptions)
      .pipe(
        tap((resp) => {
          // Clear localStorage site data
          this.siteStorageService.clearSiteData();
        }),
        map((resp) => {
          return resp.body as ApiBaseResponse;
        }),
      );
  }

  // selectIsLoggedIn(): boolean {
  // 	// return this.isAuthenticated;
  // }

  public getCurrentUser(): Observable<AuthApiLoginResponse | undefined> {
    var url: string = `${this.baseUrl}/auth/me/`;
    return this.http
      .get<AuthApiLoginResponse | undefined>(url, fHttpOptions)
      .pipe(
        catchError((error) => {
          // console.log("caught error");
          return EMPTY;
        }),
        map((resp) => {
          return resp.body as AuthApiLoginResponse | undefined;
        }),
      );
  }

  // return a Promise that returns the current user
  public loadCurrentUser(): Observable<AuthApiLoginResponse | null> {
    var url: string = `${this.baseUrl}/auth/me/`;
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      observe: "response" as const,
      withCredentials: true,
    };

    return this.http.get<AuthApiLoginResponse>(url, httpOptions).pipe(
      map((resp) => {
        if (resp.status === 200) {
          return resp.body as AuthApiLoginResponse;
        } else {
          // console.error("API request failed with status:", resp.status);
          return null; // Or a deafult value
        }
      }),
      catchError((error) => {
        // Only treat 401 (Unauthorized) as "no session — must log in".
        // Other failures (network error, 500, gateway timeout) are
        // transient and should NOT forcibly log the user out. Propagate
        // them so app-init can leave the user's auth state intact.
        if (error?.status === 401) {
          return of(null);
        }
        return throwError(() => error);
      }),
    );
  }

  // public getPermissions(siteId: string, userId: string) : Observable<AuthApiPermissionCodeListResponse> {
  // 	let params = new HttpParams()
  // 		.append('logspace', siteId)
  // 		.append('user', userId);
  // 	return this.httpClient.get<AuthApiPermissionCodeListResponse>(
  // 		`${this.baseUrl}/permissioncode/list/`, { ...fHttpOptions, observe: 'body' as const, params });
  // }

  public getPermissions(
    siteId: string,
    userId: string,
  ): Observable<AuthApiPermissionCodeListResponse> {
    let params = new HttpParams().append("sid", siteId);

    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
      observe: "response" as const,
      withCredentials: true,
      params: params,
    };

    var url: string = `${this.baseUrl}/permissioncode/list/`;
    return this.http
      .get<any>(url, httpOptions)
      .pipe(
        map((resp) => {
          // Transform array response to expected object format
          // API returns: { data: [{permissionCode: 'x', scope: '0'}, ...] }
          // Expected:    { data: {'x': {Scope: '0'}, ...} }
          const body = resp.body;
          if (body && Array.isArray(body.data)) {
            const transformedData: { [key: string]: { Scope: string | null } } = {};
            for (const item of body.data) {
              if (item.permissionCode) {
                transformedData[item.permissionCode] = { Scope: item.scope ?? null };
              }
            }
            body.data = transformedData;
          }
          return body as AuthApiPermissionCodeListResponse;
        }),
        catchError((error) => {
          console.error('[AUTH] getPermissions error:', error);
          throw error;
        }),
      );
  }

  public getUserSiteList(
    userId: string,
    filterTerms: ApiFilterTerm[],
    pageIndex1: number,
    pageSize: number,
    scope: number,
  ): Observable<AuthApiSiteListResponse> {
    let params = new HttpParams()
      .append("limit", pageSize.toString())
      .append("page", pageIndex1.toString())
      .append("scope", scope.toString());

    if (filterTerms && filterTerms.length > 0) {
      for (var term of filterTerms) {
        params = params.append("query", term.value);
      }
    }

    return this.http.get<AuthApiSiteListResponse>(
      `${this.baseUrl}/user/site-list/`,
      { params: params },
    );
  }

  /**
   * Gets the list of sites for the current user.
   * First checks local cache for cached sites, then falls back to API if empty.
   * @param userId The ID of the current user (required for API fallback)
   * @returns Observable of AuthSite array
   */
  public getSitesForCurrentUser(userId: string): Observable<AuthSite[]> {
    // First try to get sites from local cache
    const storedData = this.siteStorageService.readSiteData();
    const sitesFromCache: AuthSite[] = storedData?.sites || [];

    if (sitesFromCache.length > 0) {
      return of(sitesFromCache);
    }

    // If cache is empty, fetch from API
    return this.getUserSiteList(userId, [], 1, 100, 1).pipe(
      map((resp) =>
        resp.data.map((site) => ({
          siteId: site.siteId,
          siteName: site.siteName,
          siteAddress: site.siteAddress,
          // Additional fields can be mapped if needed
          // userRoleId, userRoleName, status, isParent, parentSiteId
        } as AuthSite))
      ),
      catchError((error) => {
        console.error("Error fetching user sites:", error);
        return of([]);
      })
    );
  }

  /**
   * Sets the user's site selection in the database session.
   * Uses POST /sessions/site_selection endpoint.
   * The X-App-ID header (set by interceptor) identifies this app for per-app site selection.
   * @param siteId The site ID to select, or null/undefined to clear selection
   * @returns Observable that completes when the selection is saved
   */
  public setSiteSelection(siteId: string | null | undefined): Observable<ApiBaseResponse> {
    const url = `${this.baseUrl}/sessions/site_selection`;
    const body = { siteId: siteId ?? null };

    return this.http
      .post<ApiBaseResponse>(url, body, fHttpOptions)
      .pipe(
        map((resp) => resp.body as ApiBaseResponse),
        catchError((error) => {
          console.error("Error setting site selection:", error);
          throw error;
        }),
      );
  }
}
